import useCurrentBuyerCompany from 'src/components/presentationals/buyer-dashboard/shared/hooks/useCurrentBuyerCompany/useCurrentBuyerCompany'
import {
  useConnectedBuyerMembership,
  useCurrentMembership,
  useCurrentSellerCompany,
  useSeller,
  useUser,
  useUserType,
} from 'src/contexts/userContext'

export interface UseOptionFlagsResult {
  optionFlagCanEditInvoiceStatus: boolean
  optionFlagCanFilterOrdersByCommercialTags: boolean
  optionFlagCanOnlyOrderMappedSpis: boolean
  optionFlagCanSetTagsOnProductLines: boolean
  optionFlagCanUseCatalog: boolean
  optionFlagCanUseChatBuyer: boolean
  optionFlagCanUseChatSeller: boolean
  optionFlagCanUseCompany: boolean
  optionFlagCanUseConsolidatedSalesOrders: boolean
  optionFlagCanUseCrossDockingPurchaseOrders: boolean
  optionFlagCanUseCustomerCategories: boolean
  optionFlagCanUseCustomerEditPrices: boolean
  optionFlagCanUseCustomers: boolean
  optionFlagCanUseDropShippingSuppliersFeature: boolean
  optionFlagCanUseManagement: boolean
  optionFlagCanUseManorSpecificReferences: boolean
  optionFlagCanUseMapping: boolean
  optionFlagCanUseMarket: boolean
  optionFlagCanUsePriceSheets: boolean
  optionFlagCanUsePurchaseInvoices: boolean
  optionFlagCanUsePurchaseOnClosedLinesFeature: boolean
  optionFlagCanUsePurchaseOrders: boolean
  optionFlagCanUseSalesInvoices: boolean
  optionFlagCanUseSalesOrders: boolean
  optionFlagCanUseSellerScheduler: boolean
  optionFlagCanUseStock: boolean
  optionFlagCanUseSuppliers: boolean
  optionFlagShowLastPurchaseDataOnSpi: boolean
  optionFlagShowMappingFeature: boolean
  optionFlagShowSupplierOpportunities: boolean
  optionFlagUseCsvPriceImportFeature: boolean
  optionFlagEnableMultiMembershipMode: boolean
}

export const useOptionFlags = (): UseOptionFlagsResult => {
  const user = useUser()
  const seller = useSeller()
  const { isBuyer, isSeller } = useUserType()
  const currentBuyerCompany = useCurrentBuyerCompany()
  const currentMembership = useCurrentMembership()
  const currentSellerCompany = useCurrentSellerCompany()
  const connectedBuyerMembership = useConnectedBuyerMembership()

  return {
    optionFlagCanEditInvoiceStatus: !!currentBuyerCompany?.optInvoiceCustomerStatusFeature,
    optionFlagShowLastPurchaseDataOnSpi:
      !!currentSellerCompany?.optShowLastPurchaseDataOnSpiFeature,
    optionFlagCanSetTagsOnProductLines: currentMembership.optSetTagsOnProductLinesFeature,
    optionFlagCanUseCatalog: currentMembership.optSellFeature,
    optionFlagCanUseChatBuyer:
      (isBuyer && !!currentMembership.optChatFeature) ||
      (!!connectedBuyerMembership?.optChatFeature &&
        currentMembership.releaseToggleAccessBuyerFeaturesAsASeller),
    optionFlagCanUseChatSeller: isSeller && !!currentMembership.optChatFeature,
    optionFlagCanUseCompany: isSeller,
    optionFlagCanUseConsolidatedSalesOrders: !!user?.optMultiMembershipsSalesOrdersFeature,
    optionFlagCanUseCrossDockingPurchaseOrders:
      !!currentMembership?.optEnableMultiMembershipMode && isSeller,
    optionFlagCanUseCustomerCategories: isSeller,
    optionFlagCanUseCustomerEditPrices: !!currentBuyerCompany?.optionFlagCustomerCanEditPrices,
    optionFlagCanUseCustomers: isSeller,
    optionFlagCanUseDropShippingSuppliersFeature: !!seller?.optionFlagDropShippingSuppliersFeature,
    optionFlagCanUseManagement: isSeller && currentMembership.optProductLinesManagementFeature,
    optionFlagCanUseManorSpecificReferences: currentMembership.optManorSpecificReferencesFeature,
    optionFlagCanUseMapping: currentMembership.optMappingFeature,
    optionFlagCanUseMarket:
      (isBuyer && currentMembership.optPurchaseFeature) ||
      !!connectedBuyerMembership?.optPurchaseFeature,
    optionFlagCanUsePriceSheets:
      currentMembership.optDisplayPriceSheetProcessingListFeature ||
      (!!connectedBuyerMembership?.optDisplayPriceSheetProcessingListFeature &&
        currentMembership.releaseToggleAccessBuyerFeaturesAsASeller),
    optionFlagCanUsePurchaseInvoices:
      isBuyer ||
      (!!connectedBuyerMembership && currentMembership.releaseToggleAccessBuyerFeaturesAsASeller),
    optionFlagCanUsePurchaseOnClosedLinesFeature: currentMembership.optPurchaseOnClosedLinesFeature,
    optionFlagCanUsePurchaseOrders:
      (currentMembership.optShowPurchaseOrdersFeature ||
        (!!connectedBuyerMembership?.optShowPurchaseOrdersFeature &&
          currentMembership.releaseToggleAccessBuyerFeaturesAsASeller)) &&
      !currentMembership.optEnableMultiMembershipMode,
    optionFlagCanUseSalesInvoices: !!seller?.optionFlagDisplayInvoicesTab,
    optionFlagCanUseSalesOrders: isSeller,
    optionFlagCanUseSellerScheduler:
      isSeller && currentMembership.optCreateOrdersForGroupSupplierFeature,
    optionFlagCanUseStock: !!seller?.canUseStockManagementModule,
    optionFlagCanUseSuppliers:
      isBuyer ||
      (!!connectedBuyerMembership && currentMembership.releaseToggleAccessBuyerFeaturesAsASeller),
    optionFlagShowMappingFeature: currentMembership.optShowMappingFeature,
    optionFlagCanOnlyOrderMappedSpis: currentMembership.optCanOnlyOrderMappedSpis,
    optionFlagCanFilterOrdersByCommercialTags:
      !!currentSellerCompany?.optFilterOrdersByCommercialTagsFeature,
    optionFlagShowSupplierOpportunities: !!currentBuyerCompany?.optionFlagShowSupplierOpportunities,
    optionFlagUseCsvPriceImportFeature: !!currentSellerCompany?.optUseCsvPriceImportFeature,
    optionFlagEnableMultiMembershipMode: currentMembership.optEnableMultiMembershipMode,
  }
}
